import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';

import { Partner, Society } from 'types';

import transactionTypes from 'constants/transactionTypes';
import { hasInternalView, hasShowDetails } from 'utils/showDetails';
import { domains } from 'constants/domains';
import { dateMatchLevels } from '../constants/dateMatchLevels';
import { checkPartnerOperationByYearAndType } from '../api/checkPartnerOperationByYearAndType';
import { getPartnerOperationsCount } from '../api/getPartnerOperationsCount';

type UseValidateIrpfPartnerProps = {
  partnerId: string;
  partner: Partner | undefined;
};

type IsInvalidPartnerProps = {
  hasCertificate: boolean;
  partnerId: string | undefined;
};

const hasIrpfCertificate = (actualSociety: Society) =>
  !!actualSociety?.templates?.irpfCertificateES;

const isDevelopmentEnviroment =
  process.env.REACT_APP_DOMAIN !== domains.AXA &&
  process.env.REACT_APP_DOMAIN !== domains.ASG;

const isLocalEnvironment = process.env.REACT_APP_DOMAIN === domains.LOCAL;

if (hasShowDetails()) {
  console.log('🚀 cclog ~ isDevelopmentEnviroment:', isDevelopmentEnviroment);
  console.log('🚀 cclog ~ isLocalEnvironment:', isLocalEnvironment);
}

const invalidCategories = [
  '67c846bb905be95b6d5f1e75', // Cotitular
  '67c846e4905be95b6d5f1e7b', // Difunto
];

const hasValidInputs = ({
  hasCertificate,
  partnerId,
}: IsInvalidPartnerProps): boolean => hasCertificate && !!partnerId;

const checkDevelopmentConditions = async (
  partner: Partner,
  partnerId: string,
  societyId: string
): Promise<boolean> => {
  const hasBuy = !!(await store.dispatch(
    checkPartnerOperationByYearAndType({
      societyId,
      partnerId,
      date: new Date('2012-06-30'),
      type: transactionTypes.CAPITAL_INCREASE,
      dateMatchLevel: dateMatchLevels.DAY,
    })
  ));

  const hasSell = !!(await store.dispatch(
    checkPartnerOperationByYearAndType({
      societyId,
      partnerId,
      date: new Date('2025-12-31'),
      type: transactionTypes.SELL,
      dateMatchLevel: dateMatchLevels.YEAR,
    })
  ));

  const hasValidCategory = !!partner?.categories?.every(
    (category) => !invalidCategories.includes(category)
  );

  if (hasShowDetails()) {
    // eslint-disable-next-line no-console
    console.log('🚀 cclog ~ checkDevelopmentConditions ~ hasBuy:', {
      hasBuy,
      hasSell,
      hasValidCategory,
    });
  }

  return hasBuy && hasSell && hasValidCategory;
};

const checkProductionConditions = async (
  partner: Partner,
  partnerId: string,
  societyId: string
): Promise<boolean> => {
  if (hasInternalView()) {
    return true;
  }

  const hasBuy = !!(await store.dispatch(
    checkPartnerOperationByYearAndType({
      societyId,
      partnerId,
      date: new Date('2012-06-30'),
      type: transactionTypes.CAPITAL_INCREASE,
      dateMatchLevel: dateMatchLevels.DAY,
    })
  ));

  const hasSell = !!(await store.dispatch(
    checkPartnerOperationByYearAndType({
      societyId,
      partnerId,
      date: new Date('2024-12-13'),
      type: transactionTypes.SELL,
      dateMatchLevel: dateMatchLevels.DAY,
    })
  ));

  const operationsCount = await store.dispatch(
    getPartnerOperationsCount({
      partnerId,
      excludedTypes: [transactionTypes.RENUMERATION],
    })
  );

  const isSimplePartner = !!(operationsCount === 2);

  const hasValidCategory = !!partner?.categories?.every(
    (category) => !invalidCategories.includes(category)
  );

  if (hasShowDetails()) {
    console.log('🚀 cclog ~ checkProductionConditions:', {
      hasBuy,
      hasSell,
      hasValidCategory,
      isSimplePartner,
      operationsCount,
    });
  }

  return hasBuy && hasSell && hasValidCategory && isSimplePartner;
};

export const useValidateIrpfPartner = ({
  partnerId,
  partner,
}: UseValidateIrpfPartnerProps): boolean => {
  const actualSociety: Society = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const [isValidPartner, setIsValidPartner] = useState(false);

  const fetchData = useCallback(async () => {
    const hasCertificate = hasIrpfCertificate(actualSociety);

    const validInputs = hasValidInputs({
      hasCertificate,
      partnerId,
    });

    if (hasShowDetails()) {
      console.log('🚀 cclog ~ fetchData ~ validInputs:', validInputs);
      console.log('🚀 cclog ~ fetchData ~ hasCertificate:', hasCertificate);
    }

    if (!validInputs) {
      setIsValidPartner(false);
      return;
    }

    try {
      const checkFn =
        isDevelopmentEnviroment || isLocalEnvironment
          ? checkDevelopmentConditions
          : checkProductionConditions;

      const isValid = await checkFn(
        partner as Partner,
        partnerId,
        actualSociety?._id
      );

      if (hasShowDetails()) {
        console.log('🚀 cclog ~ fetchData ~ checkFn:', checkFn);
        console.log('🚀 cclog ~ fetchData ~ isValid:', isValid);
      }

      setIsValidPartner(isValid);
    } catch (error) {
      console.error('Error fetching partner operation by year:', error); // eslint-disable-line no-console
      setIsValidPartner(false);
    }
  }, [actualSociety, partnerId, partner]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return isValidPartner;
};
